import React, { useState, useEffect } from 'react';
import { clienteService, usuarioService } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  Switch,
  useMediaQuery,
  TablePagination
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  Person,
  FilterList,
  People,
  TrendingUp,
  AttachMoney,
  CalendarToday,
  Visibility,
  Print,
  Download,
  Refresh,
  Assessment,
  Save,
  Cancel,
  FamilyRestroom,
  Psychology,
  Info,
  ArrowBack,
  Home,
  Work,
  PhotoCamera,
  WhatsApp,
  LocationOn,
  ContentCopy,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// =====================================================
// INTERFACES TYPESCRIPT
// =====================================================

// Função auxiliar para converter campos bit(1)
const convertBitField = (valor: any): boolean => {
  if (valor === null || valor === undefined) return false;
  if (typeof valor === 'boolean') return valor;
  if (typeof valor === 'number') return valor === 1;
  if (typeof valor === 'string') return valor === '1' || valor.toLowerCase() === 'true';
  
  // Tratamento específico para Buffer objects
  if (typeof valor === 'object' && valor !== null) {
    // Buffer serializado: {"type":"Buffer","data":[0]} ou {"type":"Buffer","data":[1]}
    if (valor.type === 'Buffer' && Array.isArray(valor.data) && valor.data.length > 0) {
      return valor.data[0] === 1;
    }
    // Buffer direto
    if (Array.isArray(valor) && valor.length > 0) {
      return valor[0] === 1;
    }
    // Outras propriedades possíveis
    if ('data' in valor) return valor.data === 1;
    if (valor[0] !== undefined) return valor[0] === 1;
  }
  
  return false;
};

const resolveClienteFotoSrc = (foto: any): string | undefined => {
  if (!foto || typeof foto !== 'string') return undefined;
  const s = foto.trim();
  if (!s || s.startsWith('data:')) return undefined;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('/')) return `https://trajetus.net${s}`;
  return `https://trajetus.net/${s}`;
};

interface Cliente {
  id: number;
  nome: string;
  email: string;
  celular: string;
  telefone?: string;
  ativo: boolean;
  foto?: string;
  dataNascimento?: string;
  genero?: string;
  tipoCliente?: string;
  tipo_cliente?: string;
  tipo_cliente_id?: number | string;
  estadoCivil?: string;
  diaAtendimento?: string;
  horaAtendimento?: string;
  valorAtendimento?: number;
  ultimaEvolucao?: string;
}

interface DeleteDialogState {
  open: boolean;
  cliente: Cliente | null;
}

interface FilterDialogState {
  open: boolean;
}

interface FiltersState {
  status: string;
  tipo: string;
  genero: string;
  estadoCivil: string;
}

interface EstatisticasState {
  total: number;
  ativos: number;
  inativos: number;
  individuais: number;
  valorTotal: number;
}

// Interfaces para o formulário
interface FormData {
  // Dados básicos
  nome: string;
  email: string;
  celular: string;
  telefone: string;
  dataNascimento: string;
  cpf: string;
  rg: string;
  uf_rg: string;
  genero_id: string | number;
  estado_civil_id: string | number;
  escolaridade_id: string | number;
  ocupacao: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  diaAtendimento: string;
  horaAtendimento: string;
  valorAtendimento: number;
  tipoCliente: string;
  ativo: boolean;
  foto: string;

  // Campos adicionais do formulário
  abordagem_id: string | number;
  emergencia_ligar_para: string;
  emergencia_fone: string;
  reside_com: string;
  nome_conjuge: string;
  idade_conjuge: string;
  escolaridade_conjuge: string;
  ocupacao_conjuge: string;
  filhos: string;

  // Aba 2 - Filiação
  nome_pai: string;
  idade_pai: string;
  ocupacao_pai: string;
  escolaridade_pai: string;
  nome_mae: string;
  idade_mae: string;
  escolaridade_mae: string;
  ocupacao_mae: string;

  // Aba 3 - Anamnese
  queixa_principal: string;
  mudancas_periodo: string;
  sintomas_sinais: string;
  acontecimento_marcante: string;
  relacionamento_familiar: string;
  outras_obs: string;
  tentativa_suicidio_sn: boolean;
  tentativa_suicidio: string;
  tratamento_psicologico_sn: boolean;
  tratamento_psicologico: string;
  alguma_doenca_sn: boolean;
  alguma_doenca: string;
  uso_medicamentos_sn: boolean;
  uso_medicamento: string;
  familiar_doenca_mental_sn: boolean;
  familiar_doenca_mental: string;
  abuso_alcool_sn: boolean;
  abuso_alcool: string;
  obs_geral: string;

  // Aba 4 - Outras Informações
  acessa_link_recadastro: boolean;
  acessa_link_experiencia: boolean;
  token: string;
  link_envio: string;
  link_experiencia_envio: string;
}

interface SelectOption {
  id: number;
  nome: string;
}

interface Errors {
  [key: string]: string | null;
}

const ClientesList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ open: false, cliente: null });
  const [filterDialog, setFilterDialog] = useState<FilterDialogState>({ open: false });
  const [filters, setFilters] = useState<FiltersState>({
    status: 'ativo',
    tipo: 'todos',
    genero: 'todos',
    estadoCivil: 'todos'
  });

  // Paginação
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalClientes, setTotalClientes] = useState<number>(0);
  const [useServerPagination, setUseServerPagination] = useState<boolean>(true);

  // Estado para dados reais dos clientes
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Estados para o formulário
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [showMapModal, setShowMapModal] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<Errors>({});
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string>('');
  const [copiedMessage, setCopiedMessage] = useState<boolean>(false);
  const [sendMessageModal, setSendMessageModal] = useState<boolean>(false);
  const [messageText, setMessageText] = useState<string>('');
  const [linkMessage, setLinkMessage] = useState<string>(''); // Nova linha
  const [sendStatus, setSendStatus] = useState<string>(''); // Novo estado para o status do envio

  // Dados do formulário
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    email: '',
    celular: '',
    telefone: '',
    dataNascimento: '',
    cpf: '',
    rg: '',
    uf_rg: '',
    genero_id: '',
    estado_civil_id: '',
    escolaridade_id: '',
    ocupacao: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    cep: '',
    diaAtendimento: '',
    horaAtendimento: '',
    valorAtendimento: 0,
    tipoCliente: '',
    ativo: true,
    foto: '',

    abordagem_id: '',
    emergencia_ligar_para: '',
    emergencia_fone: '',
    reside_com: '',
    nome_conjuge: '',
    idade_conjuge: '',
    escolaridade_conjuge: '',
    ocupacao_conjuge: '',
    filhos: '',

    nome_pai: '',
    idade_pai: '',
    ocupacao_pai: '',
    escolaridade_pai: '',
    nome_mae: '',
    idade_mae: '',
    escolaridade_mae: '',
    ocupacao_mae: '',

    queixa_principal: '',
    mudancas_periodo: '',
    sintomas_sinais: '',
    acontecimento_marcante: '',
    relacionamento_familiar: '',
    outras_obs: '',
    tentativa_suicidio_sn: false,
    tentativa_suicidio: '',
    tratamento_psicologico_sn: false,
    tratamento_psicologico: '',
    alguma_doenca_sn: false,
    alguma_doenca: '',
    uso_medicamentos_sn: false,
    uso_medicamento: '',
    familiar_doenca_mental_sn: false,
    familiar_doenca_mental: '',
    abuso_alcool_sn: false,
    abuso_alcool: '',
    obs_geral: '',

    acessa_link_recadastro: false,
    acessa_link_experiencia: false,
    token: '',
    link_envio: '',
    link_experiencia_envio: '',
  });

  // Dados para os selects
  const selectOptions = {
    generos: [
      { id: 1, nome: 'Masculino' },
      { id: 2, nome: 'Feminino' },
      { id: 3, nome: 'Outro' }
    ],
    estadosCivis: [
      { id: 1, nome: 'Solteiro(a)' },
      { id: 2, nome: 'Casado(a)' },
      { id: 3, nome: 'Divorciado(a)' },
      { id: 4, nome: 'Viúvo(a)' },
      { id: 5, nome: 'União Estável' }
    ],
    escolaridades: [
      { id: 1, nome: 'Fundamental Incompleto' },
      { id: 2, nome: 'Fundamental Completo' },
      { id: 3, nome: 'Médio Incompleto' },
      { id: 4, nome: 'Médio Completo' },
      { id: 5, nome: 'Superior Incompleto' },
      { id: 6, nome: 'Superior Completo' },
      { id: 7, nome: 'Pós-graduação' }
    ],
    abordagens: [
      { id: 1, nome: 'Gestalt' },
      { id: 2, nome: 'TCC' },
      { id: 3, nome: 'Psicanálise' }
    ],
    tiposCliente: [
      { id: 1, nome: 'Individual' },
      { id: 2, nome: 'Aluno' }, 
      { id: 3, nome: 'Casal' },
      { id: 4, nome: 'Familiar' },
      { id: 5, nome: 'Grupo' },
      { id: 6, nome: 'Avaliação Psicológica' }
    ],
    diasSemana: [
      'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 
      'Sexta-feira', 'Sábado', 'Domingo'
    ],
    ufs: [
      'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
      'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
      'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ]
  };

  // Função para mapear ID do gênero para texto
  const mapearGenero = (generoId: number | string): string => {
    const generos: { [key: string]: string } = {
      '1': 'Masculino',
      '2': 'Feminino', 
      '3': 'Outro'
    };
    return generos[String(generoId)] || 'N/A';
  };

  // Função para mapear ID do estado civil para texto
  const mapearEstadoCivil = (estadoCivilId: number | string): string => {
    const estadosCivis: { [key: string]: string } = {
      '1': 'Solteiro(a)',
      '2': 'Casado(a)',
      '3': 'Divorciado(a)',
      '4': 'Viúvo(a)',
      '5': 'União Estável'
    };
    return estadosCivis[String(estadoCivilId)] || 'N/A';
  };

  // Função para carregar clientes do backend
  const carregarClientes = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');

      const response = await clienteService.listar(
        useServerPagination
          ? { page: page + 1, limit: rowsPerPage, status: filters.status }
          : {}
      );
      console.log('🔍 [DEBUG] Resposta completa:', response.data);

      const payload = response.data;
      const dados = payload?.data || payload;

      if (dados && Array.isArray(dados)) {
        const clientesMapeados: Cliente[] = dados.map((cliente: any) => ({
          id: Number(cliente.id),
          nome: cliente.nome || '',
          email: cliente.email || '',
          dataNascimento: cliente.data_nascimento ? cliente.data_nascimento.split('T')[0] : '',
          diaAtendimento: cliente.dia_atendimento || cliente.diaAtendimento,
          horaAtendimento: cliente.hora_atendimento || cliente.horaAtendimento,
          valorAtendimento: cliente.valor_atendimento || cliente.valorAtendimento || 0,
          ultimaEvolucao: cliente.ultima_evolucao || cliente.ultimaEvolucao,
          tipoCliente: cliente.tipo_cliente || cliente.tipoCliente || '',
          tipo_cliente: cliente.tipo_cliente || cliente.tipoCliente || '',
          estadoCivil: mapearEstadoCivil(cliente.estado_civil_id || cliente.estado_civil || cliente.estadoCivil),
          celular: cliente.celular || '',
          telefone: cliente.telefone_fixo || cliente.telefone || '',
          genero: mapearGenero(cliente.genero_id || cliente.genero),
          cpf: cliente.cpf || '',
          rg: cliente.rg || '',
          endereco: cliente.endereco || '',
          cidade: cliente.cidade || '',
          uf: cliente.uf || cliente.estado || '',
          ativo: convertBitField(cliente.ativo),
          ocupacao: cliente.ocupacao || cliente.profissao || '',
          profissao: cliente.profissao || '',
          bairro: cliente.bairro || '',
          cep: cliente.cep || '',
          numero: cliente.numero || '',
          complemento: cliente.complemento || '',
          foto: resolveClienteFotoSrc(cliente.foto),
          dataCadastro: cliente.data_cadastro || cliente.dataCadastro,
          ultimaAtualizacao: cliente.ultima_atualizacao || cliente.ultimaAtualizacao
        }));

        clientesMapeados.sort((a, b) => (a.nome || '').localeCompare((b.nome || ''), 'pt-BR', { sensitivity: 'base' }));

        setClientes(clientesMapeados);

        const hasPaginationMeta =
          payload &&
          typeof payload === 'object' &&
          (payload.total !== undefined || payload.page !== undefined || payload.limit !== undefined);
        const totalFromApi = Number(payload?.total ?? clientesMapeados.length ?? 0);
        setTotalClientes(totalFromApi);

        if (useServerPagination && !hasPaginationMeta && clientesMapeados.length > rowsPerPage) {
          setUseServerPagination(false);
          setPage(0);
        }
      } else {
        console.error('❌ [DEBUG] Dados inválidos recebidos:', dados);
        setError('Dados inválidos recebidos do servidor');
      }
    } catch (error: any) {
      console.error('❌ [DEBUG] Erro ao carregar clientes:', error);
      setError(error.response?.data?.message || error.message || 'Erro ao carregar lista de clientes');
    } finally {
      setLoading(false);
    }
  };

  // Carregar clientes ao montar o componente
  useEffect(() => {
    carregarClientes();
  }, useServerPagination ? [page, rowsPerPage] : [rowsPerPage]);

  // 🔥 NOVA FUNCIONALIDADE: Recarregar lista quando retornar da tela de edição
  useEffect(() => {
    if (location.pathname === '/app/clientes') {
      console.log('🔄 Retornando para lista de clientes - recarregando dados');
      carregarClientes();
    }
  }, [location.pathname]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filters]);

  useEffect(() => {
    if (useServerPagination) {
      carregarClientes();
    }
  }, [useServerPagination, filters.status]);

  // Estatísticas dos clientes (calculadas dinamicamente)
  const isIndividual = (c: Cliente): boolean => {
    const candidates = [c.tipo_cliente, c.tipoCliente];
    for (const v of candidates) {
      if (v === undefined || v === null) continue;
      if (typeof v === 'number') {
        if (v === 1) return true;
      } else {
        const s = String(v).trim().toLowerCase();
        if (s === '1' || s.includes('individual')) return true;
        const n = Number(s);
        if (!Number.isNaN(n) && n === 1) return true;
      }
    }
    return false;
  };

  const estatisticas: EstatisticasState = {
    total: clientes.length,
    ativos: clientes.filter(c => c.ativo).length,
    inativos: clientes.filter(c => !c.ativo).length,
    individuais: clientes.filter(isIndividual).length,
    valorTotal: clientes.filter(c => c.ativo).reduce((acc, c) => acc + (Number(c.valorAtendimento) || 0), 0)
  };

  // 🔥 NOVAS OPÇÕES: Mesmas opções do formulário
  const opcoesGenero = ['Masculino', 'Feminino', 'Não binário', 'Prefere não informar'];
  const opcoesEstadoCivil = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União estável', 'Separado(a)'];
  const opcoesTipoCliente = [
    { id: 1, nome: 'Individual' },
    { id: 2, nome: 'Aluno' }, 
    { id: 3, nome: 'Casal' },
    { id: 4, nome: 'Familiar' },
    { id: 5, nome: 'Grupo' },
    { id: 6, nome: 'Avaliação Psicológica' }
  ];

  const filteredClientes = clientes
  .filter((cliente: Cliente) => {
    const matchesSearch = 
      cliente.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cliente.email && cliente.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (cliente.celular && cliente.celular.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = filters.status === 'todos' || 
      (filters.status === 'ativo' && cliente.ativo) ||
      (filters.status === 'inativo' && !cliente.ativo);
    
    // SOLUÇÃO DEFINITIVA: Testar todas as possibilidades de tipo_cliente
    let matchesTipo = filters.tipo === 'todos';
    
    if (!matchesTipo && filters.tipo !== 'todos') {
      // Teste direto com string
      if (cliente.tipoCliente === filters.tipo || cliente.tipo_cliente === filters.tipo) {
        matchesTipo = true;
      }
      
      // Teste convertendo ID para nome
      const tipoNome1 = opcoesTipoCliente.find(t => t.id === Number(cliente.tipoCliente))?.nome;
      const tipoNome2 = opcoesTipoCliente.find(t => t.id === Number(cliente.tipo_cliente))?.nome;
      
      if (tipoNome1 === filters.tipo || tipoNome2 === filters.tipo) {
        matchesTipo = true;
      }
      
      // Teste convertendo nome para ID
      const tipoId = opcoesTipoCliente.find(t => t.nome === filters.tipo)?.id;
      if (tipoId && (Number(cliente.tipoCliente) === tipoId || Number(cliente.tipo_cliente) === tipoId)) {
        matchesTipo = true;
      }
      
      // Log apenas do primeiro cliente para debug
      if (cliente === clientes[0]) {
        console.log(' [TESTE FILTRO]', {
          cliente: cliente.nome,
          tipoCliente: cliente.tipoCliente,
          tipo_cliente: cliente.tipo_cliente,
          filtro: filters.tipo,
          tipoNome1,
          tipoNome2,
          tipoId,
          resultado: matchesTipo
        });
      }
    }
    
    const matchesGenero = filters.genero === 'todos' || cliente.genero === filters.genero;
    const matchesEstadoCivil = filters.estadoCivil === 'todos' || cliente.estadoCivil === filters.estadoCivil;
    
    return matchesSearch && matchesStatus && matchesTipo && matchesGenero && matchesEstadoCivil;
  })
  .sort((a: Cliente, b: Cliente) => (a.nome || '').localeCompare((b.nome || ''), 'pt-BR', { sensitivity: 'base' }));

  const paginatedClientes = useServerPagination
    ? filteredClientes
    : filteredClientes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleEdit = (clienteId: number): void => {
    navigate(`/app/clientes/editar/${clienteId}`);
  };

  const handleView = (clienteId: number): void => {
    // Abre a ficha em nova aba SEM o Layout
    window.open(`/imprimir/cliente/${clienteId}`, '_blank');
  };

  const handleDeleteCliente = async (id: number): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      await clienteService.excluir(id);
      setClientes((prev) => prev.filter((c) => Number(c.id) !== Number(id)));
      setTotalClientes((prev) => Math.max(0, prev - 1));
      setDeleteDialog({ open: false, cliente: null });
      if (useServerPagination) {
        await carregarClientes();
      }
    } catch (error: any) {
      console.error('Erro ao deletar cliente:', error);
      setError(error?.response?.data?.message || error?.message || 'Erro ao deletar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (cliente: Cliente): void => {
    setDeleteDialog({ open: true, cliente });
  };

  const confirmDelete = (): void => {
    if (deleteDialog.cliente) {
      handleDeleteCliente(deleteDialog.cliente.id);
    }
  };

  const handlePrint = (): void => {
    window.print();
  };

  const handleExport = (): void => {
    // Implementar exportação para CSV/Excel
    alert('Funcionalidade de exportação será implementada em breve!');
  };

  const getStatusColor = (ativo: boolean): "success" | "error" => {
    return ativo ? 'success' : 'error';
  };

  const getStatusText = (ativo: boolean): string => {
    return ativo ? 'Ativo' : 'Inativo';
  };

  const getInitials = (nome: string): string => {
    return nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const calcularIdade = (dataNascimento: string | undefined): string | number => {
    if (!dataNascimento) return 'N/A';
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mesAtual = hoje.getMonth() - nascimento.getMonth();
    if (mesAtual < 0 || (mesAtual === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  };

  // Função para formatar dia da semana
  const formatarDiaSemana = (dia: string): string => {
    if (!dia) return '';
    
    const diasSemana: { [key: string]: string } = {
      'segunda': 'Seg.',
      'segunda-feira': 'Seg.',
      'terca': 'Ter.',
      'terça': 'Ter.',
      'terca-feira': 'Ter.',
      'terça-feira': 'Ter.',
      'quarta': 'Qua.',
      'quarta-feira': 'Qua.',
      'quinta': 'Qui.',
      'quinta-feira': 'Qui.',
      'sexta': 'Sex.',
      'sexta-feira': 'Sex.',
      'sabado': 'Sáb.',
      'sábado': 'Sáb.',
      'domingo': 'Dom.'
    };
    
    const diaLower = dia.toLowerCase().trim();
    return diasSemana[diaLower] || dia;
  };

  // Função para formatar hora
  const formatarHora = (hora: string): string => {
    if (!hora) return '';
    
    // Se já tem 'h' no final, retorna como está
    if (hora.endsWith('h')) return hora;
    
    // Se está no formato HH:MM, adiciona 'h'
    if (hora.match(/^\d{1,2}:\d{2}$/)) {
      return `${hora}h`;
    }
    
    // Se está no formato HH, adiciona ':00h'
    if (hora.match(/^\d{1,2}$/)) {
      return `${hora}:00h`;
    }
    
    return hora;
  };

  // =====================================================
  // FUNÇÕES DO FORMULÁRIO
  // =====================================================

  // Função para carregar dados do cliente para edição
  const carregarClienteParaEdicao = async (id: number): Promise<void> => {
    try {
      setFormLoading(true);
      console.log('🔍 [DEBUG] Carregando cliente ID:', id);
      
      const response = await clienteService.buscarPorId(id);
      console.log('🔍 [DEBUG] Resposta completa cliente:', response);
      console.log('🔍 [DEBUG] Response.data:', response.data);
      
      const cliente = response.data?.data || response.data;
      console.log('🔍 [DEBUG] Cliente extraído:', cliente);
      
      if (cliente && cliente.id) {
        console.log('🔍 [DEBUG] Cliente válido encontrado:', {
          id: cliente.id,
          nome: cliente.nome,
          email: cliente.email,
          celular: cliente.celular,
          totalCampos: Object.keys(cliente).length
        });
        
        const novoFormData = {
          nome: cliente.nome || '',
          email: cliente.email || '',
          celular: cliente.celular || '',
          telefone: cliente.telefone_fixo || cliente.telefone || '',
          dataNascimento: cliente.data_nascimento ? cliente.data_nascimento.split('T')[0] : '',
          cpf: cliente.cpf || '',
          rg: cliente.rg || '',
          uf_rg: cliente.uf_rg || '',
          genero_id: cliente.genero_id || cliente.genero || '',
          estado_civil_id: cliente.estado_civil_id || cliente.estado_civil || '',
          escolaridade_id: cliente.escolaridade_id || cliente.escolaridade || '',
          ocupacao: cliente.ocupacao || cliente.profissao || '',
          endereco: cliente.endereco || '',
          numero: cliente.numero || '',
          complemento: cliente.complemento || '',
          bairro: cliente.bairro || '',
          cidade: cliente.cidade || '',
          uf: cliente.uf || cliente.estado || '',
          cep: cliente.cep || '',
          diaAtendimento: cliente.dia_atendimento || cliente.diaAtendimento || '',
          horaAtendimento: cliente.hora_atendimento || cliente.horaAtendimento || '',
          valorAtendimento: cliente.valor_atendimento || cliente.valorAtendimento || 0,
          tipoCliente: (() => {
            const raw = cliente.tipo_cliente ?? cliente.tipoCliente ?? '';
            if (raw === '' || raw === null || raw === undefined) return '' as any;
            const asNumber = Number(raw);
            if (!Number.isNaN(asNumber)) {
              const opt = selectOptions.tiposCliente.find(t => t.id === asNumber);
              return opt ? opt.nome : String(raw);
            }
            const byName = selectOptions.tiposCliente.find(t => t.nome.toLowerCase() === String(raw).trim().toLowerCase());
            return byName ? byName.nome : String(raw);
          })(),
          ativo: convertBitField(cliente.ativo),
          foto: resolveClienteFotoSrc(cliente.foto) || '',

          abordagem_id: cliente.abordagem_id || cliente.abordagem || '',
          emergencia_ligar_para: cliente.emergencia_ligar_para || '',
          emergencia_fone: cliente.emergencia_fone || '',
          reside_com: cliente.reside_com || '',
          nome_conjuge: cliente.nome_conjuge || '',
          idade_conjuge: cliente.idade_conjuge || '',
          escolaridade_conjuge: cliente.escolaridade_conjuge || '',
          ocupacao_conjuge: cliente.ocupacao_conjuge || '',
          filhos: cliente.filhos || '',

          nome_pai: cliente.nome_pai || '',
          idade_pai: cliente.idade_pai || '',
          ocupacao_pai: cliente.ocupacao_pai || '',
          escolaridade_pai: cliente.escolaridade_pai || '',
          nome_mae: cliente.nome_mae || '',
          idade_mae: cliente.idade_mae || '',
          escolaridade_mae: cliente.escolaridade_mae || '',
          ocupacao_mae: cliente.ocupacao_mae || '',

          queixa_principal: cliente.queixa_principal || '',
          mudancas_periodo: cliente.mudancas_periodo || '',
          sintomas_sinais: cliente.sintomas_sinais || '',
          acontecimento_marcante: cliente.acontecimento_marcante || '',
          relacionamento_familiar: cliente.relacionamento_familiar || '',
          outras_obs: cliente.outras_obs || cliente.observacoes || '',
          tentativa_suicidio_sn: convertBitField(cliente.tentativa_suicidio_sn),
          tentativa_suicidio: cliente.tentativa_suicidio || '',
          tratamento_psicologico_sn: convertBitField(cliente.tratamento_psicologico_sn),
          tratamento_psicologico: cliente.tratamento_psicologico || '',
          alguma_doenca_sn: convertBitField(cliente.alguma_doenca_sn),
          alguma_doenca: cliente.alguma_doenca || '',
          uso_medicamentos_sn: convertBitField(cliente.uso_medicamentos_sn),
          uso_medicamento: cliente.uso_medicamento || '',
          familiar_doenca_mental_sn: convertBitField(cliente.familiar_doenca_mental_sn),
          familiar_doenca_mental: cliente.familiar_doenca_mental || '',
          abuso_alcool_sn: convertBitField(cliente.abuso_alcool_sn),
          abuso_alcool: cliente.abuso_alcool || '',
          obs_geral: cliente.obs_geral || cliente.observacoes || '',

          // Aplicar também para os campos de link
          acessa_link_recadastro: convertBitField(cliente.acessa_link_recadastro),
          acessa_link_experiencia: convertBitField(cliente.acessa_link_experiencia),
          token: cliente.token || '',
          link_envio: cliente.link_envio || '',
          link_experiencia_envio: cliente.link_experiencia_envio || ''
        };
        
        console.log('🔍 [DEBUG] Novo formData criado:', {
          nome: novoFormData.nome,
          email: novoFormData.email,
          celular: novoFormData.celular,
          acessa_link_recadastro: novoFormData.acessa_link_recadastro,
          acessa_link_experiencia: novoFormData.acessa_link_experiencia
        });
        
        // 🔥 ADICIONAR LOGS ESPECÍFICOS PARA ABA "OUTRAS INFORMAÇÕES"
        console.log('🔍 [DEBUG] Campos da aba OUTRAS INFORMAÇÕES:');
        console.log('- cliente.acessa_link_recadastro (raw):', cliente.acessa_link_recadastro);
        console.log('- cliente.acessa_link_experiencia (raw):', cliente.acessa_link_experiencia);
        console.log('- cliente.token:', cliente.token);
        console.log('- cliente.link_envio:', cliente.link_envio);
        console.log('- cliente.link_experiencia_envio:', cliente.link_experiencia_envio);
        console.log('- novoFormData.acessa_link_recadastro (convertido):', novoFormData.acessa_link_recadastro);
        console.log('- novoFormData.acessa_link_experiencia (convertido):', novoFormData.acessa_link_experiencia);
        console.log('- novoFormData.token:', novoFormData.token);
        console.log('- novoFormData.link_envio:', novoFormData.link_envio);
        console.log('- novoFormData.link_experiencia_envio:', novoFormData.link_experiencia_envio);
        
        console.log('🔍 [DEBUG] Executando setFormData...');
        setFormData(novoFormData);
        
        console.log('🔍 [DEBUG] setFormData executado, definindo editingId e showForm...');
        setEditingId(id);
        setShowForm(true);
        
        console.log('🔍 [DEBUG] Carregamento concluído com sucesso!');
      } else {
        console.error('❌ Cliente não encontrado ou inválido:', cliente);
        setError('Cliente não encontrado ou dados inválidos');
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar cliente:', error);
      
      // Verificar se é uma instância de Error para acessar stack e message
      if (error instanceof Error) {
        console.error('❌ Stack trace:', error.stack);
        setError('Erro ao carregar dados do cliente: ' + error.message);
      } else {
        console.error('❌ Erro desconhecido:', error);
        setError('Erro ao carregar dados do cliente: ' + String(error));
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormChange = (field: keyof FormData, value: any): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Função para formatar CPF
  const formatCPF = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Função para formatar RG (apenas números, máximo 15 dígitos)
  const formatarRG = (rg: string): string => {
    // Remove todos os caracteres que não são números
    const apenasNumeros = rg.replace(/\D/g, '');
    
    // Limita a 15 dígitos
    return apenasNumeros.slice(0, 15);
  };

  // Função para validar CPF
  const validateCPF = (cpf: string): boolean => {
    const numbers = cpf.replace(/\D/g, '');
    if (numbers.length !== 11 || /^(\d)\1{10}$/.test(numbers)) return false;
    
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(numbers.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(numbers.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(numbers.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    return remainder === parseInt(numbers.charAt(10));
  };

  // Função para formatar telefone
  const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  // Função para verificar se campo está sendo digitado e incompleto
  const isFieldIncomplete = (field: string, value: any): boolean => {
    switch (field) {
      case 'email':
        return value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'cpf':
        const cpfNumbers = value.replace(/\D/g, '');
        return cpfNumbers.length > 0 && cpfNumbers.length !== 11;
      case 'celular':
        const cellNumbers = value.replace(/\D/g, '');
        return cellNumbers.length > 0 && cellNumbers.length !== 11;
      case 'telefone_fixo':
        const phoneNumbers = value.replace(/\D/g, '');
        return phoneNumbers.length > 0 && phoneNumbers.length < 10; // 10 dígitos para fixo
      case 'rg':
        const rgNumbers = value.replace(/\D/g, '');
        return rgNumbers.length > 0 && rgNumbers.length < 7; // Mínimo 7 dígitos para RG
      default:
        return false;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validações obrigatórias - apenas Nome Completo e Celular
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    
    // Validação de email
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    // Validação de CPF
    if (formData.cpf && !validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }
    
    // Validação de RG
    if (formData.rg && formData.rg.replace(/\D/g, '').length < 7) {
      newErrors.rg = 'RG deve ter pelo menos 7 dígitos';
    }
    
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitForm = async (): Promise<void> => {
    console.log('🔍 [DEBUG] Iniciando handleSubmitForm');
    console.log('🔍 [DEBUG] FormData atual:', formData);
    
    // TEMPORARIAMENTE DESATIVANDO VALIDAÇÃO PARA TESTAR SALVAMENTO
    const isValid = true; // validateForm();
    console.log('🔍 [DEBUG] Validação DESATIVADA - forçando true');
    console.log('🔍 [DEBUG] FormData será enviado sem validação:', formData);
    
    setFormLoading(true);
    
    try {
      // Mapear TODOS os campos do frontend para o backend
        const clienteData = {
          // Dados Básicos
          nome: formData.nome?.trim(),
          cpf: formData.cpf?.trim() || null,
          rg: formData.rg?.trim() || null,
          uf_rg: formData.uf_rg?.trim() || null,
          data_nascimento: formData.dataNascimento || null,
          email: formData.email?.trim() || null,
          celular: formData.celular?.trim() || null, // ✅ CORREÇÃO: mapear celular corretamente
          telefone_fixo: formData.telefone?.trim() || null,
        
        // Endereço
        cep: formData.cep?.trim() || null,
        endereco: formData.endereco?.trim() || null,
        numero: formData.numero?.trim() || null,
        complemento: formData.complemento?.trim() || null,
        bairro: formData.bairro?.trim() || null,
        cidade: formData.cidade?.trim() || null,
        estado: formData.uf || null,
        
        // Informações Pessoais - SALVANDO COMO TEXTO
        genero: formData.genero_id || null,
        estado_civil: formData.estado_civil_id || null,
        escolaridade: formData.escolaridade_id || null,
        profissao: formData.ocupacao?.trim() || null,
        tipo_cliente: (() => {
          const v = formData.tipoCliente as any;
          if (v === null || v === undefined || v === '') return null;
          const id = Number(v);
          if (!Number.isNaN(id)) {
            const opt = selectOptions.tiposCliente.find(t => t.id === id);
            return opt ? opt.nome : String(v);
          }
          const byName = selectOptions.tiposCliente.find(t => t.nome.toLowerCase() === String(v).trim().toLowerCase());
          return byName ? byName.nome : String(v);
        })(),
        abordagem: formData.abordagem_id || null,
        
        // Contatos de Emergência
        emergencia_ligar_para: formData.emergencia_ligar_para?.trim() || null,
        emergencia_fone: formData.emergencia_fone?.trim() || null,
        
        // Informações Familiares
        reside_com: formData.reside_com?.trim() || null,
        nome_conjuge: formData.nome_conjuge?.trim() || null,
        idade_conjuge: formData.idade_conjuge?.trim() || null,
        escolaridade_conjuge: formData.escolaridade_conjuge?.trim() || null,
        ocupacao_conjuge: formData.ocupacao_conjuge?.trim() || null,
        filhos: formData.filhos?.trim() || null,
        
        // Filiação
        nome_pai: formData.nome_pai?.trim() || null,
        idade_pai: formData.idade_pai?.trim() || null,
        ocupacao_pai: formData.ocupacao_pai?.trim() || null,
        escolaridade_pai: formData.escolaridade_pai || null,
        nome_mae: formData.nome_mae?.trim() || null,
        idade_mae: formData.idade_mae?.trim() || null,
        escolaridade_mae: formData.escolaridade_mae || null,
        ocupacao_mae: formData.ocupacao_mae?.trim() || null,

        // Anamnese
        queixa_principal: formData.queixa_principal?.trim() || null,
        mudancas_periodo: formData.mudancas_periodo?.trim() || null,
        sintomas_sinais: formData.sintomas_sinais?.trim() || null,
        acontecimento_marcante: formData.acontecimento_marcante?.trim() || null,
        relacionamento_familiar: formData.relacionamento_familiar?.trim() || null,
        outras_obs: formData.outras_obs?.trim() || null,
        // Campos booleanos da anamnese - CONVERTER PARA 0/1
        tentativa_suicidio_sn: formData.tentativa_suicidio_sn ? 1 : 0,
        tentativa_suicidio: formData.tentativa_suicidio?.trim() || null,
        tratamento_psicologico_sn: formData.tratamento_psicologico_sn ? 1 : 0,
        tratamento_psicologico: formData.tratamento_psicologico?.trim() || null,
        alguma_doenca_sn: formData.alguma_doenca_sn ? 1 : 0,
        alguma_doenca: formData.alguma_doenca?.trim() || null,
        uso_medicamentos_sn: formData.uso_medicamentos_sn ? 1 : 0,
        uso_medicamento: formData.uso_medicamento?.trim() || null,
        familiar_doenca_mental_sn: formData.familiar_doenca_mental_sn ? 1 : 0,
        familiar_doenca_mental: formData.familiar_doenca_mental?.trim() || null,
        abuso_alcool_sn: formData.abuso_alcool_sn ? 1 : 0,
        abuso_alcool: formData.abuso_alcool?.trim() || null,
        observacoes: formData.obs_geral?.trim() || null,
        
        // Atendimento
        dia_atendimento: formData.diaAtendimento || null,
        hora_atendimento: formData.horaAtendimento || null,
        valor_atendimento: formData.valorAtendimento ? parseFloat(formData.valorAtendimento.toString()) : null,
        
        // Status e Links - CORRIGIR CONVERSÃO DE BOOLEANOS
        ativo: formData.ativo !== undefined ? (formData.ativo ? 1 : 0) : 1,
        acessa_link_recadastro: formData.acessa_link_recadastro ? 1 : 0,
        acessa_link_experiencia: formData.acessa_link_experiencia ? 1 : 0,
        token: formData.token?.trim() || null,
        link_envio: formData.link_envio?.trim() || null,
        link_experiencia_envio: formData.link_experiencia_envio?.trim() || null,
        
        // Foto e Usuário
        foto: formData.foto || null,
        id_usuario: user?.id
      };
      
      console.log('🚀 [DEBUG] Dados do cliente:', clienteData);
      console.log('🚀 [DEBUG] EditingId:', editingId);
      console.log('🚀 [DEBUG] Method:', editingId ? 'PUT' : 'POST');
      
      let response;
      if (editingId) {
        response = await clienteService.atualizar(editingId, clienteData);
      } else {
        response = await clienteService.criar(clienteData);
      }
      
      console.log('✅ [DEBUG] Cliente salvo:', response.data?.data || response.data);
      
      // Fechar formulário e recarregar lista
      handleCloseForm();
      await carregarClientes();
      
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      setFormErrors({ general: `Erro ao salvar cliente: ${error instanceof Error ? error.message : 'Erro desconhecido'}` });
    } finally {
      setFormLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    setActiveTab(newValue);
  };

  // Busca de endereço por CEP
  const buscarEnderecoPorCEP = async (cep: string): Promise<void> => {
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        
        if (!data.erro) {
          handleFormChange('endereco', data.logradouro);
          handleFormChange('bairro', data.bairro);
          handleFormChange('cidade', data.localidade);
          handleFormChange('uf', data.uf);
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  // Função para formatar número para API do WhatsApp
  const formatarNumeroParaWAPI = (numero: string): string | null => {
    const numeroLimpo = numero.replace(/\D/g, '');
    
    if (numeroLimpo.length === 11) {
      // Formato: 11987654321 -> 5511987654321
      return `55${numeroLimpo}`;
    } else if (numeroLimpo.length === 10) {
      // Formato: 1187654321 -> 551187654321
      return `55${numeroLimpo}`;
    }
    
    return null;
  };

  // Busca de foto do WhatsApp
  const buscarFotoWhatsApp = async (): Promise<void> => {
    try {
      if (!formData.celular) {
        setError('Por favor, preencha o celular primeiro.');
        return;
      }

      console.log('🔍 [DEBUG] Iniciando busca foto WhatsApp para:', formData.celular);

      // Buscar dados do usuário aziel@sistemap.com.br para obter instancia e token
      const usuariosResponse = await usuarioService.listar();
      console.log('🔍 [DEBUG] Resposta usuários:', usuariosResponse);
      
      // CORREÇÃO: Acessar usuariosResponse.data.data (onde está o array)
      const usuarios = usuariosResponse?.data?.data || [];
      console.log('🔍 [DEBUG] Lista de usuários:', usuarios.length);
      
      const usuarioAziel = usuarios.find((u: any) => u.email === 'aziel@sistemap.com.br');
      console.log('🔍 [DEBUG] Usuário Aziel encontrado:', !!usuarioAziel);
      
      if (!usuarioAziel) {
        setError('Usuário aziel@sistemap.com.br não encontrado para obter configurações do WhatsApp.');
        return;
      }

      console.log('🔍 [DEBUG] Dados do usuário Aziel:', {
        email: usuarioAziel.email,
        temInstancia: !!usuarioAziel.instancia_whatsapp,
        temToken: !!usuarioAziel.token_whatsapp
      });

      const instancia = usuarioAziel.instancia_whatsapp;
      const token = usuarioAziel.token_whatsapp;

      if (!instancia || !token) {
        setError('Usuário aziel@sistemap.com.br não tem instância e token do WhatsApp configurados.');
        return;
      }

      const numeroFormatado = formatarNumeroParaWAPI(formData.celular);
      console.log('🔍 [DEBUG] Número formatado:', numeroFormatado);
      
      if (!numeroFormatado) {
        setError('Formato de número inválido! Use: (11) 98765-4321');
        return;
      }

      console.log('🔍 Buscando foto do WhatsApp:', {
        instanceId: instancia,
        phoneNumber: numeroFormatado,
        token: token ? '***' : 'NÃO CONFIGURADO'
      });

      // Fazer requisição para a API do WhatsApp
      const response = await fetch(`https://api.w-api.app/v1/contacts/profile-picture?instanceId=${instancia}&phoneNumber=${numeroFormatado}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📡 Resposta da API WhatsApp:', response.status, response.statusText);

      if (response.ok) {
        const resultado = await response.json();
        console.log('📱 Resultado da API:', resultado);
        
        // A API retorna a URL no campo "link"
        if (resultado.link && !resultado.link.isNull) {
          console.log('✅ Foto encontrada: ' + resultado.link);
          
          // Baixar a imagem da URL retornada
          try {
            const imagemResponse = await fetch(resultado.link);
            if (imagemResponse.ok) {
              const blob = await imagemResponse.blob();
              const reader = new FileReader();
              reader.onload = (e) => {
                const fotoBase64 = e.target?.result as string;
                handleFormChange('foto', fotoBase64);
                setSuccess('Foto do WhatsApp carregada com sucesso!');
                setTimeout(() => setSuccess(''), 3000);
              };
              reader.readAsDataURL(blob);
            } else {
              setError('Erro ao baixar a imagem do WhatsApp.');
            }
          } catch (error) {
            console.error('❌ Erro ao baixar imagem:', error);
            setError('Erro ao baixar a imagem do WhatsApp.');
          }
        } else {
          setError('Nenhuma foto encontrada para este número no WhatsApp.');
        }
      } else {
        // Tratamento de erro
        let mensagemErro = '';
        if (response.status === 401) {
          mensagemErro = 'Token inválido ou expirado - Verifique configuração do usuário aziel@sistemap.com.br';
        } else if (response.status === 400) {
          mensagemErro = 'Instância não encontrada ou inválida - Verifique configuração do usuário aziel@sistemap.com.br';
        } else if (response.status === 500) {
          mensagemErro = 'Erro interno da API - usuário pode ter bloqueado a captura da foto';
        } else {
          const errorText = await response.text();
          mensagemErro = `Erro HTTP ${response.status}: ${errorText}`;
        }
        
        console.error('❌ Erro na resposta:', response.status, mensagemErro);
        setError(`Erro ao buscar foto do WhatsApp: ${mensagemErro}`);
        
        // Se for erro 500 (usuário bloqueou), remover mensagem após 6 segundos
        if (response.status === 500) {
          setTimeout(() => setError(''), 6000);
        }
      }
    } catch (error) {
      console.error('💥 Erro ao buscar foto do WhatsApp:', error);
      setError('Erro ao buscar foto do WhatsApp. Verifique o console para mais detalhes.');
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      // Verificar se é uma imagem
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem.');
        return;
      }
      
      // Verificar tamanho do arquivo (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('O arquivo deve ter no máximo 5MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        handleFormChange('foto', e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = (): void => {
    handleFormChange('foto', '');
  };

  // Função para copiar o link de recadastro
  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(formData.link_envio || '');
      setCopiedMessage(true);
      setTimeout(() => setCopiedMessage(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar link:', err);
    }
  };

  // Função para abrir o modal e gerar a mensagem personalizada
  const handleOpenSendMessageModal = (): void => {
    const firstName = formData.nome ? formData.nome.split(' ')[0] : 'Cliente';
    const linkRecadastro = formData.link_envio || '';
    
    const defaultMessage = `Olá ${firstName},\nsegue abaixo o link para concluir o seu cadastro, verifique as 3 abas da tela. A senha para acessar são os ultimos 4 digitos do seu Celular.`;
    
    setMessageText(defaultMessage);
    setLinkMessage(linkRecadastro);
    setSendMessageModal(true);
  };

  // Função para abrir o modal do Link de Experiência
  const handleOpenSendExperienceModal = (): void => {
    const firstName = formData.nome ? formData.nome.split(' ')[0] : 'Cliente';
    const linkExperiencia = formData.link_experiencia_envio || '';
    
    const defaultMessage = `Olá ${firstName},\nsegue abaixo o link para registrar suas experiências diárias, estarei analizando suas mensagens para nosso próximo atendimento. A senha para acessar são os ultimos 4 digitos do seu Celular.`;
    
    setMessageText(defaultMessage);
    setLinkMessage(linkExperiencia);
    setSendMessageModal(true);
  };

  // Função para fechar o modal
  const handleCloseSendMessageModal = (): void => {
    setSendMessageModal(false);
    setMessageText('');
    setLinkMessage('');
  };

  // Função para enviar a mensagem
  const handleSendMessage = async (): Promise<void> => {
    try {
      setSendStatus('Enviando a primeira mensagem...');
      
      // Buscar dados do usuário aziel@sistemap.com.br para obter instancia e token
      const usuariosResponse = await usuarioService.listar();
      console.log('🔍 [DEBUG] Resposta usuários:', usuariosResponse);
      
      // CORREÇÃO: Acessar usuariosResponse.data.data (onde está o array)
      const usuarios = usuariosResponse?.data?.data || [];
      console.log('🔍 [DEBUG] Lista de usuários:', usuarios.length);
      
      const usuarioAziel = usuarios.find((u: any) => u.email === 'aziel@sistemap.com.br');
      console.log('🔍 [DEBUG] Usuário Aziel encontrado:', !!usuarioAziel);
      
      if (!usuarioAziel) {
        setSendStatus('');
        setError('Usuário aziel@sistemap.com.br não encontrado para obter configurações do WhatsApp.');
        return;
      }

      console.log('🔍 [DEBUG] Dados do usuário Aziel:', {
        email: usuarioAziel.email,
        temInstancia: !!usuarioAziel.instancia_whatsapp,
        temToken: !!usuarioAziel.token_whatsapp
      });

      const instancia = usuarioAziel.instancia_whatsapp;
      const token = usuarioAziel.token_whatsapp;

      if (!instancia || !token) {
        setSendStatus('');
        setError('Usuário aziel@sistemap.com.br não tem instância e token do WhatsApp configurados.');
        return;
      }

      // Formatar número do celular - CORRIGIDO: usar mesma função da captura de foto
      const numeroFormatado = formatarNumeroParaWAPI(formData.celular);
      
      if (!numeroFormatado) {
        setSendStatus('');
        setError('Formato de número inválido! Use: (11) 98765-4321');
        return;
      }
      
      console.log('📱 [DEBUG] Número original:', formData.celular);
      console.log('📱 [DEBUG] Número formatado:', numeroFormatado);
      console.log('📱 [DEBUG] Tamanho do número formatado:', numeroFormatado.length);

      // URL da API wAPI
      const apiUrl = `https://api.w-api.app/v1/message/send-text?instanceId=${instancia}`;
      
      // Headers para a requisição
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // Dados da primeira mensagem - CORRIGIDO: usar 'message' ao invés de 'text'
      const firstMessageData = {
        phone: numeroFormatado,
        message: messageText,
        delayMessage: 2
      };

      // Dados da segunda mensagem - CORRIGIDO: usar 'message' ao invés de 'text'
      const secondMessageData = {
        phone: numeroFormatado,
        message: linkMessage,
        delayMessage: 2
      };

      console.log('📱 [DEBUG] Número usado:', numeroFormatado);
      console.log('📱 [DEBUG] Primeira mensagem:', firstMessageData);
      console.log('📱 [DEBUG] Segunda mensagem:', secondMessageData);

      // Enviar primeira mensagem (Mensagem 1)
      console.log('📤 Enviando primeira mensagem...');
      const response1 = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(firstMessageData)
      });

      if (!response1.ok) {
        const errorData = await response1.text();
        setSendStatus('');
        throw new Error(`Erro ao enviar primeira mensagem: ${response1.status} - ${errorData}`);
      }

      console.log('✅ Primeira mensagem enviada com sucesso');

      // Aguardar 4 segundos
      setSendStatus('Enviando a 2ª mensagem...');
      console.log('⏳ Aguardando 4 segundos...');
      await new Promise(resolve => setTimeout(resolve, 4000));

      // Enviar segunda mensagem (Mensagem 2 - Link)
      console.log('📤 Enviando segunda mensagem...');
      const response2 = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(secondMessageData)
      });

      if (!response2.ok) {
        const errorData = await response2.text();
        setSendStatus('');
        throw new Error(`Erro ao enviar segunda mensagem: ${response2.status} - ${errorData}`);
      }

      console.log('✅ Segunda mensagem enviada com sucesso');

      // Exibir mensagem de sucesso
      setSendStatus('Mensagens enviadas com sucesso!');
      setSuccess('Mensagens enviadas com sucesso!');
      
      // Aguardar 4 segundos antes de limpar o status e fechar o modal
      setTimeout(() => {
        setSendStatus('');
        handleCloseSendMessageModal();
      }, 4000);

    } catch (error) {
      console.error('❌ Erro ao enviar mensagens:', error);
      setSendStatus('');
      setError(`Erro ao enviar mensagens: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  // Funções para abrir/fechar formulário
  const handleNewCliente = (): void => {
    setFormData({
      nome: '',
      email: '',
      celular: '',
      telefone: '',
      dataNascimento: '',
      cpf: '',
      rg: '',
      uf_rg: '',
      genero_id: '',
      estado_civil_id: '',
      escolaridade_id: '',
      ocupacao: '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      uf: '',
      cep: '',
      diaAtendimento: '',
      horaAtendimento: '',
      valorAtendimento: 0,
      tipoCliente: '',
      ativo: true,
      foto: '',

      abordagem_id: '',
      emergencia_ligar_para: '',
      emergencia_fone: '',
      reside_com: '',
      nome_conjuge: '',
      idade_conjuge: '',
      escolaridade_conjuge: '',
      ocupacao_conjuge: '',
      filhos: '',

      nome_pai: '',
      idade_pai: '',
      ocupacao_pai: '',
      escolaridade_pai: '',
      nome_mae: '',
      idade_mae: '',
      escolaridade_mae: '',
      ocupacao_mae: '',

      queixa_principal: '',
      mudancas_periodo: '',
      sintomas_sinais: '',
      acontecimento_marcante: '',
      relacionamento_familiar: '',
      outras_obs: '',
      tentativa_suicidio_sn: false,
      tentativa_suicidio: '',
      tratamento_psicologico_sn: false,
      tratamento_psicologico: '',
      alguma_doenca_sn: false,
      alguma_doenca: '',
      uso_medicamentos_sn: false,
      uso_medicamento: '',
      familiar_doenca_mental_sn: false,
      familiar_doenca_mental: '',
      abuso_alcool_sn: false,
      abuso_alcool: '',
      obs_geral: '',

      acessa_link_recadastro: false,
      acessa_link_experiencia: false,
      token: '',
      link_envio: '',
      link_experiencia_envio: '',
    });
    setEditingId(null);
    setActiveTab(0);
    setFormErrors({});
    setShowForm(true);
  };

  const handleEditCliente = (clienteId: number): void => {
    setEditingId(clienteId);
    setActiveTab(0);
    setFormErrors({});
    setShowForm(true);
    carregarClienteParaEdicao(clienteId);
  };

  const handleCloseForm = (): void => {
    setShowForm(false);
    setEditingId(null);
    setActiveTab(0);
    setFormErrors({});
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Conteúdo principal da lista de clientes */}
      {!showForm && (
        <Box sx={{ 
          pb: 4,
          maxWidth: '100vw',
          mx: 'auto'
        }}>
      {/* Header com Estatísticas */}
      <Box sx={{ mb: 3 }}>
        {/* Linha do título com botão voltar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          position: 'relative',
          mb: 2 
        }}>
          {/* Botão Voltar - Posicionado no canto esquerdo */}
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              position: 'absolute',
              left: 0,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.light + '20'
              }
            }}
            size="large"
          >
            <ArrowBack />
          </IconButton>
          
          {/* Título centralizado */}
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: theme.palette.primary.main,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
          }}>
            👥 Gestão de Clientes
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{
          mb: 2,
          fontSize: { xs: '0.875rem', sm: '1rem' },
          textAlign: 'center'
        }}>
          Gerencie seus clientes de forma eficiente e profissional
        </Typography>
        
        {/* Indicador de Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Carregando clientes...
            </Typography>
          </Box>
        )}
        
        {/* Mensagem de Erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, maxWidth: '600px', mx: 'auto' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {/* Mensagem de Sucesso */}
        {success && (
          <Alert severity="success" sx={{ mb: 2, maxWidth: '600px', mx: 'auto' }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        
        {/* Cards de Estatísticas - RESPONSIVIDADE REFEITA */}
        <Box sx={{ 
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: { xs: 2, lg: 1 },
          mb: 3,
          px: { xs: 2, sm: 3, md: 4, lg: 2 }
        }}>
          {/* Card 1 - Total de Clientes */}
            <Card sx={{ 
            width: { xs: '310px', sm: '230px', md: '210px', lg: '170px' },
              background: `linear-gradient(135deg, #1976d2, #1565c0)`,
              color: 'white',
              textAlign: 'center'
            }}>
            <CardContent sx={{ p: 1.5 }}>
              <People sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h4" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                mb: 0.5,
                color: 'white'
              }}>{estatisticas.total}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Total de Clientes
              </Typography>
              </CardContent>
            </Card>
          
          {/* Card 2 - Clientes Ativos */}
            <Card sx={{ 
            width: { xs: '310px', sm: '230px', md: '210px', lg: '170px' },
              background: `linear-gradient(135deg, #4caf50, #388e3c)`,
              color: 'white',
              textAlign: 'center'
            }}>
            <CardContent sx={{ p: 1.5 }}>
              <TrendingUp sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h4" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                mb: 0.5,
                color: 'white'
              }}>{estatisticas.ativos}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Clientes Ativos
              </Typography>
              </CardContent>
            </Card>
          
          {/* Card 3 - Individuais */}
            <Card sx={{ 
            width: { xs: '310px', sm: '230px', md: '210px', lg: '170px' },
              background: `linear-gradient(135deg, #ff9800, #f57c00)`,
              color: 'white',
              textAlign: 'center'
            }}>
            <CardContent sx={{ p: 1.5 }}>
              <Person sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h4" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                mb: 0.5,
                color: 'white'
              }}>{estatisticas.individuais}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Individuais
              </Typography>
              </CardContent>
            </Card>
          
          {/* Card 4 - Valor Total */}
            <Card sx={{ 
            width: { xs: '310px', sm: '230px', md: '210px', lg: '170px' },
              background: `linear-gradient(135deg, #e91e63, #c2185b)`,
              color: 'white',
              textAlign: 'center'
            }}>
            <CardContent sx={{ p: 1.5 }}>
              <AttachMoney sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h4" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                mb: 0.5,
                color: 'white'
              }}>R$ {Number(estatisticas.valorTotal || 0).toFixed(0)}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Valor Total
              </Typography>
              </CardContent>
            </Card>
          
          {/* Card 5 - Ticket Médio */}
            <Card sx={{ 
            width: { xs: '310px', sm: '230px', md: '210px', lg: '170px' },
              background: `linear-gradient(135deg, #607d8b, #455a64)`,
              color: 'white',
              textAlign: 'center'
            }}>
            <CardContent sx={{ p: 1.5 }}>
              <Assessment sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h4" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                mb: 0.5,
                color: 'white'
              }}>{Number(estatisticas.ativos > 0 ? (estatisticas.valorTotal / estatisticas.ativos) : 0).toFixed(0)}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Ticket Médio
              </Typography>
              </CardContent>
            </Card>
        </Box>
      </Box>

      {/* Toolbar - RESPONSIVIDADE REFEITA */}
      <Card sx={{ mb: 3, boxShadow: 3, mx: { xs: 1, sm: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1.5, sm: 2 },
            flexWrap: 'wrap'
          }}>
            {/* Campo de Busca */}
              <TextField
                placeholder="🔍 Buscar por nome, email ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ 
                width: { xs: '250px', sm: '300px', md: '350px' },
                maxWidth: '100%'
              }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              
            {/* Botão Filtros */}
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setFilterDialog({ open: true })}
              size="small"
              sx={{ 
                width: { xs: '120px', sm: 'auto' },
                minWidth: { xs: '120px', sm: '100px', md: '120px' }
              }}
              >
                Filtros
              </Button>

            {/* Botão Refresh */}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={carregarClientes}
              disabled={loading}
              size="small"
              sx={{ 
                width: { xs: '120px', sm: 'auto' },
                minWidth: { xs: '120px', sm: '100px', md: '120px' }
              }}
            >
              Atualizar
            </Button>

            {/* Botão Novo Cliente */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleNewCliente}
              size="small"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                },
                width: { xs: '150px', sm: 'auto' },
                minWidth: { xs: '150px', sm: 'auto' },
                px: { xs: 2, sm: 2.5, md: 3 },
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }
              }}
            >
              ✨ Novo Cliente
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tabela de Clientes */}
      <Card sx={{ boxShadow: 3, mx: { xs: 1, sm: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 0, sm: 2, md: 3 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Carregando clientes...
              </Typography>
            </Box>
          ) : paginatedClientes.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Person sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Nenhum cliente encontrado com os filtros aplicados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Tente ajustar os filtros ou a busca para encontrar clientes.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setFilters({ status: 'todos', tipo: 'todos', genero: 'todos', estadoCivil: 'todos' });
                }}
                size="large"
              >
                Limpar Filtros
              </Button>
            </Box>
          ) : (
          <>
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: '#a8a8a8',
                },
              },
            }}
          >
            <Table sx={{ minWidth: { xs: 800, sm: 900, md: 1000 } }}>
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: theme.palette.primary.main, 
                  color: 'white',
                  height: { xs: 'auto', sm: 'calc(100% - 8px)' }, 
                  '& .MuiTableCell-root': {
                    py: { xs: 1, sm: 1.5 } 
                  }
                }}>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    px: { xs: 1.5, sm: 2 },
                    minWidth: { xs: 180, sm: 200 }
                  }}>Cliente</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    px: { xs: 1.5, sm: 2 },
                    minWidth: { xs: 160, sm: 180 }
                  }}>Contato</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: { xs: 140, sm: 160 }
                  }}>Atendimento</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    px: { xs: 1.5, sm: 2 },
                    minWidth: { xs: 160, sm: 180 },
                    whiteSpace: 'nowrap'
                  }}>Evolução</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: 'white', 
                    textAlign: 'center',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    px: { xs: 1.5, sm: 2 },
                    minWidth: { xs: 120, sm: 140 },
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    bgcolor: theme.palette.primary.main
                  }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedClientes.map((cliente: Cliente) => (
                  <TableRow key={cliente.id} hover>
                    <TableCell sx={{ px: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={resolveClienteFotoSrc(cliente.foto)}
                          sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            bgcolor: theme.palette.primary.main,
                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                            fontWeight: 600
                          }}
                        >
                          {!cliente.foto ? getInitials(cliente.nome) : null}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                            {cliente.nome}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                            {cliente.email || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ px: { xs: 1.5, sm: 2 } }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {cliente.celular || cliente.telefone || 'N/A'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ px: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {cliente.diaAtendimento || 'Não definido'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          R$ {Number(cliente.valorAtendimento || 0).toFixed(2).replace('.', ',')}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{
                      px: { xs: 1.5, sm: 2 },
                      minWidth: { xs: 140, sm: 160 },
                      whiteSpace: 'nowrap'
                    }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        whiteSpace: 'nowrap'
                      }}>
                        {cliente.ultimaEvolucao ? new Date(cliente.ultimaEvolucao).toLocaleDateString('pt-BR') : 'N/A'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{
                      px: { xs: 1.5, sm: 2 },
                      position: 'sticky',
                      right: 0,
                      zIndex: 1,
                      bgcolor: 'background.paper'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 1 } }}>
                        <Tooltip title="Visualizar">
                          <IconButton
                            size={isMobile ? "small" : "small"}
                            onClick={() => handleView(cliente.id)}
                            sx={{ 
                              color: theme.palette.info.main,
                              '&:hover': { bgcolor: 'info.light', color: 'white' },
                              px: 0.5,
                              ml: 0
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Editar">
                          <IconButton
                            size={isMobile ? "small" : "small"}
                            onClick={() => handleEditCliente(cliente.id)}
                            sx={{ 
                              color: theme.palette.primary.main,
                              '&:hover': { bgcolor: 'primary.light', color: 'white' },
                              px: 0.5
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Excluir">
                          <IconButton
                            size={isMobile ? "small" : "small"}
                            onClick={() => handleDelete(cliente)}
                            sx={{ 
                              color: theme.palette.error.main,
                              '&:hover': { bgcolor: 'error.light', color: 'white' },
                              px: 0.5
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={useServerPagination ? totalClientes : filteredClientes.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const next = Number(e.target.value) || 10;
              setRowsPerPage(next);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
          </>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, cliente: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          ⚠️ Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta ação não pode ser desfeita!
          </Alert>
          <Typography>
            Tem certeza que deseja excluir o cliente <strong>{deleteDialog.cliente?.nome}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Todos os dados relacionados a este cliente serão permanentemente removidos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, cliente: null })}>
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Filtros */}
      <Dialog
        open={filterDialog.open}
        onClose={() => setFilterDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>🔍 Filtros Avançados</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0, pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  label="Status"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="ativo">Ativos</MenuItem>
                  <MenuItem value="inativo">Inativos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="tipo-label">Tipo de Cliente</InputLabel>
                <Select
                  labelId="tipo-label"
                  label="Tipo de Cliente"
                  value={filters.tipo}
                  onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  {opcoesTipoCliente.map(tipo => (
                    <MenuItem key={tipo.id} value={tipo.nome}>{tipo.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="genero-label">Gênero</InputLabel>
                <Select
                  labelId="genero-label"
                  label="Gênero"
                  value={filters.genero}
                  onChange={(e) => setFilters({ ...filters, genero: e.target.value })}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  {opcoesGenero.map(genero => (
                    <MenuItem key={genero} value={genero}>{genero}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="estado-civil-label">Estado Civil</InputLabel>
                <Select
                  labelId="estado-civil-label"
                  label="Estado Civil"
                  value={filters.estadoCivil}
                  onChange={(e) => setFilters({ ...filters, estadoCivil: e.target.value })}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  {opcoesEstadoCivil.map(estado => (
                    <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilters({ status: 'todos', tipo: 'todos', genero: 'todos', estadoCivil: 'todos' })}>
            Limpar Filtros
          </Button>
          <Button onClick={() => setFilterDialog({ open: false })} variant="contained">
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      )}

      {/* Formulário de Cliente - Layout igual ao ClienteForm.js original */}
      {showForm && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: { xs: 0, md: 240 }, // Espaço para o menu apenas no desktop
          right: 0, 
          bottom: 0, 
          bgcolor: 'background.default', 
          zIndex: 1300, 
          overflow: 'auto',
          p: { xs: 2, sm: 3 }
        }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: { xs: 2, sm: 3 }
          }}>
            <IconButton onClick={handleCloseForm} sx={{ mr: { xs: 1, sm: 2 } }}>
              <ArrowBack />
            </IconButton>
            <Typography 
              variant="h4" 
              sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem' } }}
            >
              {editingId ? 'Editar Cliente' : 'Novo Cliente'}
            </Typography>
          </Box>

          {formErrors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {formErrors.general}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Card>
            <CardContent>
                {/* Tabs */}
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                >
                  <Tab label="Dados" icon={<Person />} iconPosition="start" />
                  <Tab label="Filiação" icon={<FamilyRestroom />} iconPosition="start" />
                  <Tab label="Anamnese" icon={<Psychology />} iconPosition="start" />
                  <Tab label="Outras Informações" icon={<Info />} iconPosition="start" />
                </Tabs>

                {/* Conteúdo das Abas */}
                {/* ABA 1 - DADOS */}
                {activeTab === 0 && (
                  <Grid container spacing={3}>
                    {/* Campo de Foto e Campos de Atendimento */}
                    <Grid item xs={12} md={6} sx={{ textAlign: 'center', mb: 2 }}>
                      <Avatar 
                        src={formData.foto} 
                        sx={{ 
                          width: 120, 
                          height: 120, 
                          mx: 'auto', 
                          mb: 1,
                          bgcolor: theme.palette.primary.main,
                          fontSize: '2rem'
                        }}
                      >
                        {!formData.foto && getInitials(formData.nome || '')}
                      </Avatar>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<PhotoCamera />}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          {formData.foto ? 'Alterar Foto' : 'Adicionar Foto'}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handlePhotoUpload}
                          />
                        </Button>
                        {/* Botão WhatsApp removido */}
                        {formData.foto && (
                          <Button
                            variant="text"
                            color="error"
                            size="small"
                            onClick={handleRemovePhoto}
                            sx={{ textTransform: 'none' }}
                          >
                            Remover
                          </Button>
                        )}
                      </Box>
                    </Grid>

                    {/* Campos de Atendimento ao lado da foto */}
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Dia do Atendimento</InputLabel>
                            <Select
                              value={formData.diaAtendimento}
                              onChange={(e) => handleFormChange('diaAtendimento', e.target.value)}
                              label="Dia do Atendimento"
                            >
                              <MenuItem value="Segunda-feira">Segunda-feira</MenuItem>
                              <MenuItem value="Terça-feira">Terça-feira</MenuItem>
                              <MenuItem value="Quarta-feira">Quarta-feira</MenuItem>
                              <MenuItem value="Quinta-feira">Quinta-feira</MenuItem>
                              <MenuItem value="Sexta-feira">Sexta-feira</MenuItem>
                              <MenuItem value="Sábado">Sábado</MenuItem>
                              <MenuItem value="Domingo">Domingo</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Hora do Atendimento"
                            type="time"
                            value={formData.horaAtendimento}
                            onChange={(e) => handleFormChange('horaAtendimento', e.target.value)}
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Valor do Atendimento"
                            value={formData.valorAtendimento}
                            onChange={(e) => handleFormChange('valorAtendimento', e.target.value)}
                            variant="outlined"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Tipo de Cliente</InputLabel>
                            <Select
                              value={formData.tipoCliente}
                              onChange={(e) => handleFormChange('tipoCliente', e.target.value)}
                              label="Tipo de Cliente"
                            >
                              {selectOptions.tiposCliente.map(tipo => (
                                <MenuItem key={tipo.id} value={tipo.id}>
                                  {tipo.nome}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Celular *"
                            value={formData.celular}
                            onChange={(e) => {
                              const formatted = formatPhone(e.target.value);
                              if (formatted.replace(/\D/g, '').length <= 11) {
                                handleFormChange('celular', formatted);
                              }
                            }}
                            error={!!formErrors.celular || isFieldIncomplete('celular', formData.celular)}
                            helperText={formErrors.celular || (isFieldIncomplete('celular', formData.celular) ? 'Celular incompleto' : '')}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Phone />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title="Capturar foto do WhatsApp">
                                    <span>
                                      <IconButton
                                        onClick={buscarFotoWhatsApp}
                                        disabled={formLoading || !formData.celular}
                                        size="small"
                                      >
                                        <WhatsApp />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Telefone Fixo"
                            value={formData.telefone}
                            onChange={(e) => {
                              const formatted = formatPhone(e.target.value);
                              if (formatted.replace(/\D/g, '').length <= 11) {
                                handleFormChange('telefone', formatted);
                              }
                            }}
                            error={isFieldIncomplete('telefone_fixo', formData.telefone)}
                            helperText={isFieldIncomplete('telefone_fixo', formData.telefone) ? 'Telefone incompleto' : ''}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Phone />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                
                <Grid item xs={12} md={9}>
                  <TextField
                    fullWidth
                    label="Nome Completo *"
                    value={formData.nome}
                    onChange={(e) => handleFormChange('nome', e.target.value)}
                    error={!!formErrors.nome}
                    helperText={formErrors.nome}
                    variant="outlined"
                    InputProps={{
                      style: { fontWeight: 'bold' }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Data de Nascimento"
                    type="date"
                    value={formData.dataNascimento}
                    onChange={(e) => handleFormChange('dataNascimento', e.target.value)}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="CPF"
                    value={formData.cpf}
                    onChange={(e) => {
                      const formatted = formatCPF(e.target.value);
                      if (formatted.replace(/\D/g, '').length <= 11) {
                        handleFormChange('cpf', formatted);
                      }
                    }}
                    error={!!formErrors.cpf || isFieldIncomplete('cpf', formData.cpf)}
                    helperText={formErrors.cpf || (isFieldIncomplete('cpf', formData.cpf) ? 'CPF inválido' : '')}
                    variant="outlined"
                    placeholder="000.000.000-00"
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="RG"
                    value={formData.rg}
                    onChange={(e) => {
                      const formatted = formatarRG(e.target.value);
                      handleFormChange('rg', formatted);
                    }}
                    error={!!formErrors.rg || isFieldIncomplete('rg', formData.rg)}
                    helperText={formErrors.rg || (isFieldIncomplete('rg', formData.rg) ? 'RG deve ter pelo menos 7 dígitos' : '')}
                    variant="outlined"
                    placeholder="Apenas números"
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Select
                            value={formData.uf_rg}
                            onChange={(e) => handleFormChange('uf_rg', e.target.value)}
                            variant="standard"
                            disableUnderline
                            displayEmpty
                            sx={{ 
                              minWidth: 60,
                              fontSize: '0.875rem',
                              '& .MuiSelect-select': {
                                paddingRight: '20px !important',
                                paddingLeft: '8px',
                                paddingTop: '4px',
                                paddingBottom: '4px'
                              }
                            }}
                          >
                            <MenuItem value="" sx={{ fontSize: '0.875rem' }}>UF</MenuItem>
                            <MenuItem value="AC" sx={{ fontSize: '0.875rem' }}>AC</MenuItem>
                            <MenuItem value="AL" sx={{ fontSize: '0.875rem' }}>AL</MenuItem>
                            <MenuItem value="AP" sx={{ fontSize: '0.875rem' }}>AP</MenuItem>
                            <MenuItem value="AM" sx={{ fontSize: '0.875rem' }}>AM</MenuItem>
                            <MenuItem value="BA" sx={{ fontSize: '0.875rem' }}>BA</MenuItem>
                            <MenuItem value="CE" sx={{ fontSize: '0.875rem' }}>CE</MenuItem>
                            <MenuItem value="DF" sx={{ fontSize: '0.875rem' }}>DF</MenuItem>
                            <MenuItem value="ES" sx={{ fontSize: '0.875rem' }}>ES</MenuItem>
                            <MenuItem value="GO" sx={{ fontSize: '0.875rem' }}>GO</MenuItem>
                            <MenuItem value="MA" sx={{ fontSize: '0.875rem' }}>MA</MenuItem>
                            <MenuItem value="MT" sx={{ fontSize: '0.875rem' }}>MT</MenuItem>
                            <MenuItem value="MS" sx={{ fontSize: '0.875rem' }}>MS</MenuItem>
                            <MenuItem value="MG" sx={{ fontSize: '0.875rem' }}>MG</MenuItem>
                            <MenuItem value="PA" sx={{ fontSize: '0.875rem' }}>PA</MenuItem>
                            <MenuItem value="PB" sx={{ fontSize: '0.875rem' }}>PB</MenuItem>
                            <MenuItem value="PR" sx={{ fontSize: '0.875rem' }}>PR</MenuItem>
                            <MenuItem value="PE" sx={{ fontSize: '0.875rem' }}>PE</MenuItem>
                            <MenuItem value="PI" sx={{ fontSize: '0.875rem' }}>PI</MenuItem>
                            <MenuItem value="RJ" sx={{ fontSize: '0.875rem' }}>RJ</MenuItem>
                            <MenuItem value="RN" sx={{ fontSize: '0.875rem' }}>RN</MenuItem>
                            <MenuItem value="RS" sx={{ fontSize: '0.875rem' }}>RS</MenuItem>
                            <MenuItem value="RO" sx={{ fontSize: '0.875rem' }}>RO</MenuItem>
                            <MenuItem value="RR" sx={{ fontSize: '0.875rem' }}>RR</MenuItem>
                            <MenuItem value="SC" sx={{ fontSize: '0.875rem' }}>SC</MenuItem>
                            <MenuItem value="SP" sx={{ fontSize: '0.875rem' }}>SP</MenuItem>
                            <MenuItem value="SE" sx={{ fontSize: '0.875rem' }}>SE</MenuItem>
                            <MenuItem value="TO" sx={{ fontSize: '0.875rem' }}>TO</MenuItem>
                          </Select>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth error={!!formErrors.genero_id}>
                    <InputLabel>Gênero</InputLabel>
                    <Select
                      value={formData.genero_id}
                      onChange={(e) => handleFormChange('genero_id', e.target.value)}
                      label="Gênero"
                    >
                      {selectOptions.generos.map(genero => (
                        <MenuItem key={genero.id} value={genero.id}>
                          {genero.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth error={!!formErrors.estado_civil_id}>
                    <InputLabel>Estado Civil</InputLabel>
                    <Select
                      value={formData.estado_civil_id}
                      onChange={(e) => handleFormChange('estado_civil_id', e.target.value)}
                      label="Estado Civil"
                    >
                      {selectOptions.estadosCivis.map(estado => (
                        <MenuItem key={estado.id} value={estado.id}>
                          {estado.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Escolaridade</InputLabel>
                    <Select
                      value={formData.escolaridade_id}
                      onChange={(e) => handleFormChange('escolaridade_id', e.target.value)}
                      label="Escolaridade"
                    >
                      {selectOptions.escolaridades.map(escolaridade => (
                        <MenuItem key={escolaridade.id} value={escolaridade.id}>
                          {escolaridade.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Ocupação"
                    value={formData.ocupacao}
                    onChange={(e) => handleFormChange('ocupacao', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Work />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Contato
                  </Typography>
                </Grid>
                
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    error={!!formErrors.email || isFieldIncomplete('email', formData.email)}
                    helperText={formErrors.email || (isFieldIncomplete('email', formData.email) ? 'Email inválido' : '')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Endereço
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={2.5}>
                  <TextField
                    fullWidth
                    label="CEP"
                    value={formData.cep}
                    onChange={(e) => {
                      const cep = e.target.value.replace(/\D/g, '');
                      handleFormChange('cep', cep);
                      if (cep.length === 8) {
                        buscarEnderecoPorCEP(cep);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Tooltip title="Visualizar endereço">
                            <IconButton
                              onClick={() => setShowMapModal(true)}
                              size="small"
                              disabled={!formData.endereco || !formData.numero}
                            >
                              <LocationOn />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Capturar endereço">
                            <IconButton
                              onClick={() => {
                                if (formData.cep && formData.cep.length === 8) {
                                  buscarEnderecoPorCEP(formData.cep);
                                }
                              }}
                              edge="end"
                              size="small"
                            >
                              <Search />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    placeholder="00000-000"
                  />
                </Grid>
                
                <Grid item xs={12} md={7.5}>
                  <TextField
                    fullWidth
                    label="Endereço"
                    value={formData.endereco}
                    onChange={(e) => handleFormChange('endereco', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Home />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Número"
                    value={formData.numero}
                    onChange={(e) => handleFormChange('numero', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={2.5}>
                  <TextField
                    fullWidth
                    label="Complemento"
                    value={formData.complemento}
                    onChange={(e) => handleFormChange('complemento', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Bairro"
                    value={formData.bairro}
                    onChange={(e) => handleFormChange('bairro', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={3.5}>
                  <TextField
                    fullWidth
                    label="Cidade"
                    value={formData.cidade}
                    onChange={(e) => handleFormChange('cidade', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>UF</InputLabel>
                    <Select
                      value={formData.uf}
                      onChange={(e) => handleFormChange('uf', e.target.value)}
                      label="UF"
                    >
                      {selectOptions.ufs.map(uf => (
                        <MenuItem key={uf} value={uf}>
                          {uf}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Informações Adicionais
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Em caso de emergência, ligar para"
                    value={formData.emergencia_ligar_para}
                    onChange={(e) => handleFormChange('emergencia_ligar_para', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Telefone de emergência"
                    value={formData.emergencia_fone}
                    onChange={(e) => handleFormChange('emergencia_fone', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Reside com"
                    value={formData.reside_com}
                    onChange={(e) => handleFormChange('reside_com', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nome do Cônjuge"
                    value={formData.nome_conjuge}
                    onChange={(e) => handleFormChange('nome_conjuge', e.target.value)}
                  />
                </Grid>

                {/* Informações do Cônjuge */}
                {(formData.estado_civil_id === 2 || formData.estado_civil_id === 5) && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Informações do Cônjuge
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nome do Cônjuge"
                        value={formData.nome_conjuge}
                        onChange={(e) => handleFormChange('nome_conjuge', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Idade do Cônjuge"
                        value={formData.idade_conjuge}
                        onChange={(e) => handleFormChange('idade_conjuge', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Escolaridade do Cônjuge"
                        value={formData.escolaridade_conjuge}
                        onChange={(e) => handleFormChange('escolaridade_conjuge', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Ocupação do Cônjuge"
                        value={formData.ocupacao_conjuge}
                        onChange={(e) => handleFormChange('ocupacao_conjuge', e.target.value)}
                      />
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Filhos"
                    multiline
                    rows={3}
                    value={formData.filhos}
                    onChange={(e) => handleFormChange('filhos', e.target.value)}
                    placeholder="Descreva informações sobre os filhos (nomes, idades, etc.)"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.ativo}
                        onChange={(e) => handleFormChange('ativo', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Cliente Ativo"
                    sx={{ mt: 2 }}
                  />
                </Grid>
              </Grid>
            )}

            {/* ABA 2 - FILIAÇÃO */}
            {activeTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Informações dos Pais
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nome do Pai"
                    value={formData.nome_pai}
                    onChange={(e) => handleFormChange('nome_pai', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Idade do Pai"
                    value={formData.idade_pai}
                    onChange={(e) => handleFormChange('idade_pai', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Ocupação do Pai"
                    value={formData.ocupacao_pai}
                    onChange={(e) => handleFormChange('ocupacao_pai', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Escolaridade do Pai"
                    value={formData.escolaridade_pai}
                    onChange={(e) => handleFormChange('escolaridade_pai', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nome da Mãe"
                    value={formData.nome_mae}
                    onChange={(e) => handleFormChange('nome_mae', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Idade da Mãe"
                    value={formData.idade_mae}
                    onChange={(e) => handleFormChange('idade_mae', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Ocupação da Mãe"
                    value={formData.ocupacao_mae}
                    onChange={(e) => handleFormChange('ocupacao_mae', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Escolaridade da Mãe"
                    value={formData.escolaridade_mae}
                    onChange={(e) => handleFormChange('escolaridade_mae', e.target.value)}
                  />
                </Grid>
              </Grid>
            )}

            {/* ABA 3 - ANAMNESE */}
            {activeTab === 2 && (
              <Grid container spacing={3}>
                {/* Campo de Motivo Principal */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Motivo Principal"
                    multiline
                    rows={4}
                    value={formData.queixa_principal}
                    onChange={(e) => handleFormChange('queixa_principal', e.target.value)}
                    placeholder="Descreva o motivo principal da busca por atendimento psicológico"
                  />
                </Grid>
                
                {/* Campo de Mudanças no Período */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mudanças no Período"
                    multiline
                    rows={3}
                    value={formData.mudancas_periodo}
                    onChange={(e) => handleFormChange('mudancas_periodo', e.target.value)}
                    placeholder="Descreva mudanças significativas ocorridas recentemente"
                  />
                </Grid>
                
                {/* Campo de Sintomas e Sinais */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Sintomas e Sinais"
                    multiline
                    rows={3}
                    value={formData.sintomas_sinais}
                    onChange={(e) => handleFormChange('sintomas_sinais', e.target.value)}
                    placeholder="Descreva sintomas físicos e psicológicos observados"
                  />
                </Grid>
                
                {/* Campo de Acontecimento Marcante */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Acontecimento Marcante"
                    multiline
                    rows={3}
                    value={formData.acontecimento_marcante}
                    onChange={(e) => handleFormChange('acontecimento_marcante', e.target.value)}
                    placeholder="Descreva eventos significativos na vida do cliente"
                  />
                </Grid>
                
                {/* Campo de Relacionamento Familiar */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Relacionamento Familiar"
                    multiline
                    rows={3}
                    value={formData.relacionamento_familiar}
                    onChange={(e) => handleFormChange('relacionamento_familiar', e.target.value)}
                    placeholder="Descreva a dinâmica e qualidade dos relacionamentos familiares"
                  />
                </Grid>

                {/* Campo de Histórico Clínico */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Histórico Clínico
                  </Typography>
                </Grid>
                
                {/* Campos de Histórico Clínico */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.abuso_alcool_sn}
                          onChange={(e) => handleFormChange('abuso_alcool_sn', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label="Faz uso abusivo de álcool ou outras substâncias?"
                      sx={{ mb: 1 }}
                    />
                    {formData.abuso_alcool_sn && (
                      <TextField
                        fullWidth
                        label="Descreva o uso de substâncias"
                        multiline
                        rows={2}
                        value={formData.abuso_alcool}
                        onChange={(e) => handleFormChange('abuso_alcool', e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="Detalhe o tipo de substância, frequência e quantidade"
                      />
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.alguma_doenca_sn}
                          onChange={(e) => handleFormChange('alguma_doenca_sn', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label="Possui alguma doença?"
                      sx={{ mb: 1 }}
                    />
                    {formData.alguma_doenca_sn && (
                      <TextField
                        fullWidth
                        label="Descreva a doença"
                        multiline
                        rows={2}
                        value={formData.alguma_doenca}
                        onChange={(e) => handleFormChange('alguma_doenca', e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="Informe o tipo de doença e tratamentos"
                      />
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.familiar_doenca_mental_sn}
                          onChange={(e) => handleFormChange('familiar_doenca_mental_sn', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label="Há histórico de doença mental na família?"
                      sx={{ mb: 1 }}
                    />
                    {formData.familiar_doenca_mental_sn && (
                      <TextField
                        fullWidth
                        label="Descreva o histórico familiar"
                        multiline
                        rows={2}
                        value={formData.familiar_doenca_mental}
                        onChange={(e) => handleFormChange('familiar_doenca_mental', e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="Informe o grau de parentesco e tipo de doença mental"
                      />
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.tentativa_suicidio_sn}
                          onChange={(e) => handleFormChange('tentativa_suicidio_sn', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.error.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.error.main,
                            },
                          }}
                        />
                      }
                      label="Já teve tentativa de suicídio?"
                      sx={{ mb: 1, color: theme.palette.error.main }}
                    />
                    {formData.tentativa_suicidio_sn && (
                      <TextField
                        fullWidth
                        label="Descreva a tentativa de suicídio"
                        multiline
                        rows={2}
                        value={formData.tentativa_suicidio}
                        onChange={(e) => handleFormChange('tentativa_suicidio', e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="Informe quando ocorreu e as circunstâncias"
                        error
                      />
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.tratamento_psicologico_sn}
                          onChange={(e) => handleFormChange('tratamento_psicologico_sn', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label="Já fez tratamento psicológico anteriormente?"
                      sx={{ mb: 1 }}
                    />
                    {formData.tratamento_psicologico_sn && (
                      <TextField
                        fullWidth
                        label="Descreva o tratamento psicológico anterior"
                        multiline
                        rows={2}
                        value={formData.tratamento_psicologico}
                        onChange={(e) => handleFormChange('tratamento_psicologico', e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="Informe o tipo de tratamento, duração e resultados"
                      />
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.uso_medicamentos_sn}
                          onChange={(e) => handleFormChange('uso_medicamentos_sn', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label="Faz uso de medicamentos?"
                      sx={{ mb: 1 }}
                    />
                    {formData.uso_medicamentos_sn && (
                      <TextField
                        fullWidth
                        label="Descreva os medicamentos em uso"
                        multiline
                        rows={2}
                        value={formData.uso_medicamento}
                        onChange={(e) => handleFormChange('uso_medicamento', e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="Informe nome, dosagem e finalidade dos medicamentos"
                      />
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* ABA 4 - OUTRAS INFORMAÇÕES */}
            {activeTab === 3 && (
              <Grid container spacing={3}>
                {/* Campo Token */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Token"
                    value={formData.token || ''}
                    onChange={(e) => {
                      const newToken = e.target.value;
                      handleFormChange('token', newToken);
                      // Atualizar automaticamente o link de recadastro quando token for alterado
                      if (newToken.trim()) {
                        handleFormChange('link_envio', `https://trajetus.net/meu-cadastro/${newToken}`);
                      } else {
                        handleFormChange('link_envio', '');
                      }
                      // Atualizar automaticamente o link de experiência
                      if (newToken.trim()) {
                        handleFormChange('link_experiencia_envio', `https://trajetus.net/experiencia/${newToken}`);
                      } else {
                        handleFormChange('link_experiencia_envio', '');
                      }
                    }}
                    placeholder="Token de acesso externo (35 caracteres)"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Gerar token automático" arrow>
                            <IconButton
                              onClick={() => {
                                const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                                let result = '';
                                for (let i = 0; i < 35; i++) {
                                  result += chars.charAt(Math.floor(Math.random() * chars.length));
                                }
                                handleFormChange('token', result);
                                // Atualizar automaticamente o link de recadastro
                                handleFormChange('link_envio', `https://trajetus.net/meu-cadastro/${result}`);
                                // Atualizar automaticamente o link de experiência
                                handleFormChange('link_experiencia_envio', `https://trajetus.net/experiencia/${result}`);
                              }}
                              size="small"
                            >
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary', textAlign: 'center' }}>
                    Clique no ícone para gerar token automático de 35 caracteres
                  </Typography>
                </Grid>

                {/* Campo booleano para Link de Recadastro */}
                <Grid item xs={12}>
                  <FormControlLabel
                      control={
                        <Switch
                          checked={formData.acessa_link_recadastro === true}
                          onChange={(e) => handleFormChange('acessa_link_recadastro', e.target.checked)}
                        />
                      }
                      label="Acessa Link de Recadastro"
                    />
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Permite que o cliente acesse o link de recadastro
                  </Typography>
                </Grid>

                {/* Campo Link de Recadastro sempre visível */}
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      fullWidth
                      label="Link de Recadastro"
                      value={formData.link_envio || ''}
                      onChange={(e) => handleFormChange('link_envio', e.target.value)}
                      placeholder="URL para recadastro do cliente"
                      multiline
                      rows={2}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copiar link">
                              <IconButton
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(formData.link_envio || '');
                                    setCopiedMessage(true);
                                    setTimeout(() => setCopiedMessage(false), 2000);
                                  } catch (err) {
                                    console.error('Erro ao copiar link:', err);
                                  }
                                }}
                                disabled={!formData.link_envio}
                                size="small"
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar link via WhatsApp">
                              <IconButton
                                onClick={handleOpenSendMessageModal}
                                disabled={!formData.link_envio || !formData.celular}
                                size="small"
                                sx={{
                                  color: '#25D366',
                                  '&:hover': {
                                    backgroundColor: 'rgba(37, 211, 102, 0.1)'
                                  },
                                  '&:disabled': {
                                    color: '#cccccc'
                                  }
                                }}
                              >
                                <WhatsApp />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                </Grid>
                
                {/* Campo booleano para Link de Experiência */}
                <Grid item xs={12}>
                  <FormControlLabel
                      control={
                        <Switch
                          checked={formData.acessa_link_experiencia === true}
                          onChange={(e) => handleFormChange('acessa_link_experiencia', e.target.checked)}
                        />
                      }
                      label="Acessa Link de Experiência"
                    />
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Permite que o cliente registre experiências diárias
                  </Typography>
                </Grid>

                {/* Campo Link de Experiência condicional */}
                {formData.acessa_link_experiencia && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Link de Experiência"
                      value={formData.link_experiencia_envio || ''}
                      onChange={(e) => handleFormChange('link_experiencia_envio', e.target.value)}
                      placeholder="URL para envio de experiências"
                      multiline
                      rows={2}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copiar link">
                              <IconButton
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(formData.link_experiencia_envio || '');
                                    setCopiedMessage(true);
                                    setTimeout(() => setCopiedMessage(false), 2000);
                                  } catch (err) {
                                    console.error('Erro ao copiar link:', err);
                                  }
                                }}
                                disabled={!formData.link_experiencia_envio}
                                size="small"
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar link via WhatsApp">
                              <IconButton
                                onClick={handleOpenSendExperienceModal}
                                disabled={!formData.link_experiencia_envio || !formData.celular}
                                size="small"
                                sx={{
                                  color: '#25D366',
                                  '&:hover': {
                                    backgroundColor: 'rgba(37, 211, 102, 0.1)'
                                  },
                                  '&:disabled': {
                                    color: '#cccccc'
                                  }
                                }}
                              >
                                <WhatsApp />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Um link será gerado para que o cliente possa registrar suas experiências diárias.
                  </Typography>
                </Grid>

                {/* Campo de observações gerais */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações Gerais"
                    multiline
                    rows={4}
                    value={formData.obs_geral || ''}
                    onChange={(e) => handleFormChange('obs_geral', e.target.value)}
                    placeholder="Informações adicionais relevantes sobre o cliente"
                  />
                </Grid>
              </Grid>
            )}

            {/* Botões de Ação */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCloseForm}
                disabled={formLoading}
                sx={{ textTransform: 'none' }}
              >
                Cancelar
              </Button>
              
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={() => {
                  console.log('🔥 [DEBUG] BOTÃO SALVAR CLICADO!');
                  handleSubmitForm();
                }}
                disabled={formLoading}
                sx={{
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  },
                }}
              >
                {formLoading ? 'Salvando...' : 'Salvar Cliente'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Modal do Mapa */}
      <Dialog
        open={showMapModal}
        onClose={() => setShowMapModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Visualizar Endereço no Mapa
        </DialogTitle>
        <DialogContent>
          {formData.endereco && formData.numero ? (
            <Box sx={{ height: '400px', width: '100%' }}>
              <iframe
                width="100%"
                height="100%"
                style={{ border: 0 }}
                src={`https://www.google.com/maps/embed/v1/place?key=YOUR_GOOGLE_MAPS_API_KEY&q=${encodeURIComponent(`${formData.endereco}, ${formData.numero}, ${formData.bairro}, ${formData.cidade}, ${formData.uf}`)}`}
                allowFullScreen
              />
            </Box>
          ) : (
            <Alert severity="warning">
              Preencha os campos Endereço e Número para visualizar no mapa.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMapModal(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para envio de mensagem */}
      <Dialog
        open={sendMessageModal}
        onClose={handleCloseSendMessageModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Enviar para o Cliente
        </DialogTitle>
        <DialogContent>
          {/* Informações do Cliente */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
            <TextField
              label="Nome do Cliente"
              value={formData.nome || ''}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
              }}
              sx={{ 
                flex: 2,
                '& .MuiInputBase-input': {
                  fontWeight: 'bold',
                  backgroundColor: '#F5E6D3',
                  cursor: 'default'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5E6D3'
                }
              }}
            />
            <TextField
              label="Celular"
              value={formData.celular || ''}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
              }}
              sx={{ 
                flex: 1,
                '& .MuiInputBase-input': {
                  fontWeight: 'bold',
                  backgroundColor: '#F5E6D3',
                  cursor: 'default'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5E6D3'
                }
              }}
            />
          </Box>
          
          {/* Status do envio */}
          {sendStatus && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {sendStatus}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Mensagem 1"
            multiline
            rows={4}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Mensagem 2"
            multiline
            rows={2}
            value={linkMessage}
            onChange={(e) => setLinkMessage(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSendMessageModal} color="secondary">
            Cancelar
          </Button>
          <Button 
            onClick={handleSendMessage} 
            variant="contained" 
            color="primary"
            disabled={!messageText.trim() && !linkMessage.trim() || !!sendStatus}
          >
            {sendStatus ? 'Enviando...' : 'Enviar Mensagem'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientesList;